import { css } from 'aphrodite';
import React, { useState } from 'react';
import ReactModal from 'react-modal';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import {
	ITemplate,
	RejectionCategory,
	ReviewStatus,
	TemplatesViewModel,
	TemplateType,
} from '../../../../../extViewmodels';
import {
	DefaultRichContentRawCSS,
	ICreateCampaignRequest,
	ILocationState,
	TemplateOrTemplateFilter,
} from '../../../../../models';
import { useFullscreenModal, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { FirstNamePlaceholder } from '../../../../../models/Token';
import {
	checkSocialCitation,
	convertRawRichTextContentStateToRichContentEditorState,
} from '../../../../../models/UiUtils';
import { invalidateEmailAllCategoryTemplates, invalidateEmailCategoryTemplates } from '../../../../../queries';
import { useCreateTemplateReviewMutation } from '../../../../../queries/Templates/useCreateTemplateReviewMutation';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../../../components/MultiContainerHeader';
import { RichContentDocumentEditor } from '../../../../components/richContent/RichContentDocumentEditor';
import { SocialMediaBannerGraphicLight } from '../../../../components/svgs/graphics/SocialMediaBannerGraphicLight';
import { getTemplateApprovalStatus } from '../../../../components/TemplateReviewStatusIndicator';
import { TemplateRejectionModal } from '../../../../components/templates/TemplateRejectionModal';
import { baseStyleSheet } from '../../../../styles/styles';
import { getEnabledReviewApprovalStatus } from '../utils';
import { styles } from './styles';
import { TemplateRejectionReason } from './types';

export const ReviewEmailCampaign: React.FC = () => {
	const history = useHistory();
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const loc: ILocationState<ITemplate, any> = history.location.state;
	const template = loc?.model?.context as ITemplate;

	const editorConfig = {
		contentRawCss: DefaultRichContentRawCSS,
		plugins: 'placeholders',
		useDefaultEmailPlaceholders: true,
	};

	const [showCitations, setShowCitations] = useState(false);
	const [showRejectionModal, setShowRejectionModal] = useState(false);

	const { mutate: createTemplateReview, isPending: isReviewLoading } = useCreateTemplateReviewMutation();

	const invalidateTemplates = () => {
		invalidateEmailAllCategoryTemplates();
		invalidateEmailCategoryTemplates();
	};

	const handleApprove = () => {
		const enabledStatus = getEnabledReviewApprovalStatus(userSession.account);
		if (!enabledStatus) {
			toast.error('No template approval status is enabled on this account.');
			return;
		}

		createTemplateReview(
			{
				templateId: template.id,
				reviewRequest: { id: template.id, status: enabledStatus, reason: null },
			},
			{
				onSuccess: () => {
					invalidateTemplates();
					toast.success('Template approved successfully.');
					fullscreenModal.dismissModal();
				},
				onError: () => {
					invalidateTemplates();
					toast.error('Failed to approve the template.');
				},
			}
		);
	};

	const handleReject = (reasoning: string, rejectionCategory: RejectionCategory) =>
		createTemplateReview(
			{
				templateId: template.id,
				reviewRequest: {
					id: template.id,
					status: ReviewStatus.Rejected,
					reason: { reasoning, rejectionCategory } as TemplateRejectionReason,
				},
			},
			{
				onSuccess: () => {
					invalidateTemplates();
					toast.success('Template rejected successfully.');
					fullscreenModal.dismissModal();
				},
				onError: () => {
					invalidateTemplates();
					toast.error('Failed to reject the template.');
				},
			}
		);

	const handleViewCitations = () => {
		setShowCitations(true);
	};

	const handleCloseCitations = () => {
		setShowCitations(false);
	};

	const renderEmailComposerFooter = () => {
		const message = `${FirstNamePlaceholder.symbol} is a placeholder for recipient names; actual names will be populated.`;
		return (
			<div className={css(baseStyleSheet.truncateText, styles.emailComposerFooter)} title={message}>
				{message}
			</div>
		);
	};

	const approvalStatus = getTemplateApprovalStatus(template?.templateReview);
	const hasAssociatedTemplates = template?.associatedTemplates?.length > 0;
	const isRejected = template?.templateReview?.status === ReviewStatus.Rejected;
	const isApproved =
		template?.templateReview?.status === ReviewStatus.PreApproved ||
		template?.templateReview?.status === ReviewStatus.PreReviewed;
	return (
		<div className={css(styles.container)}>
			<MultiContainerHeader fullscreenHeader='Review Email Campaign' />
			<div className={css(styles.flex)}>
				<div className={css(styles.leftContainer)}>
					<div className={css(styles.previewContainerHeader)}>
						<div className={css(baseStyleSheet.horizontalStack, baseStyleSheet.gap4)}>
							<div>{template?.name}</div>
							{approvalStatus}
						</div>
						<div className={css(baseStyleSheet.horizontalStack, baseStyleSheet.gap4)}>
							{hasAssociatedTemplates && (
								<div className={css(styles.socialMediaBannerContainer)}>
									<button
										onClick={async () => {
											const socialMediaTemplateReference = template?.associatedTemplates?.find(
												x => x.templateType === TemplateType.SocialMediaPost
											);

											const templates = new TemplatesViewModel(userSession);
											const fetchedTemplate = await templates.getByIdExpandedByLastUsedBy(
												socialMediaTemplateReference.id
											);

											const locationState: ILocationState<any, ICreateCampaignRequest<TemplateOrTemplateFilter>> = {
												model: {
													context: { ...fetchedTemplate },
													type: 'Template',
												},
											};
											fullscreenModal?.history.replace({
												pathname: `/social-media/post/review/from-template`,
												state: locationState,
											});
										}}
									>
										<SocialMediaBannerGraphicLight />
										<span className={css(styles.bannerText)}>Review related Social Media Post</span>
									</button>
								</div>
							)}
							{checkSocialCitation(template) && (
								<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={handleViewCitations}>
									<span>View Citations</span>
								</button>
							)}
						</div>
					</div>
					<div className={css(styles.previewContainer)}>
						<div className={css(baseStyleSheet.truncateText)}>
							<div className={css(styles.composerTitle)}>Subject:</div>
							<div className={css(baseStyleSheet.truncateText, styles.subject)}>{template?.subject}</div>
						</div>
						<div className={css(baseStyleSheet.truncateText)}>
							<div className={css(styles.composerTitle)}>Message:</div>
						</div>
						<RichContentDocumentEditor
							config={editorConfig}
							contentState={convertRawRichTextContentStateToRichContentEditorState(template?.content)}
							readOnly={true}
						/>
					</div>
					{renderEmailComposerFooter()}
				</div>

				<div className={css(styles.rightContainer)}>
					<div>
						<div className={css(styles.header)}>
							<h3 className={css(styles.title)}>Campaign Approval</h3>
							<p className={css(styles.subtitle)}>Preview campaign for compliance approval</p>
							<hr className={css(styles.divider)} />
						</div>

						{isRejected && template?.templateReview?.reason && (
							<div className={css(styles.rejectionInfo)}>
								<div className={css(styles.rejectionReasonSection)}>
									<p className={css(styles.rejectionReasonTitle)}>Reason for rejection:</p>
									<ul className={css(styles.rejectionCategoryList)}>
										<li className={css(styles.rejectionCategoryItem)}>
											{'- ' + template.templateReview.reason.rejectionCategory}
										</li>
									</ul>
								</div>

								<div className={css(styles.rejectionNotesSection)}>
									<p className={css(styles.rejectionNotesTitle)}>Notes:</p>
									<p className={css(styles.rejectionNotesContent)}>{template.templateReview.reason.reasoning}</p>
								</div>
							</div>
						)}
					</div>

					<div className={css(styles.footer)}>
						{!isApproved && (
							<button className={css(baseStyleSheet.ctaButton)} onClick={handleApprove} disabled={isReviewLoading}>
								{isReviewLoading ? <LoadingSpinner type='small' /> : <span>Approve Campaign</span>}
							</button>
						)}
						{!isRejected && (
							<button
								className={css(baseStyleSheet.ctaButtonReverse, styles.rejectButton)}
								onClick={() => setShowRejectionModal(true)}
								disabled={isReviewLoading}
							>
								{isReviewLoading ? <LoadingSpinner type='small' /> : <span>Reject Campaign</span>}
							</button>
						)}
					</div>
				</div>
			</div>

			<TemplateRejectionModal
				isOpen={showRejectionModal}
				onRequestClose={() => setShowRejectionModal(false)}
				onSubmit={handleReject}
			/>

			<ReactModal
				isOpen={showCitations}
				onRequestClose={handleCloseCitations}
				className={css(styles.citationModal)}
				overlayClassName={css(styles.modalOverlay)}
			>
				<div className={css(styles.citationContainer)}>
					<h3 className={css(styles.citationTitle)}>View Citations</h3>
					<p className={css(styles.citationContent)}>{template?.citation || 'No citation available.'}</p>
					<button className={css(baseStyleSheet.ctaButton)} onClick={handleCloseCitations}>
						Close
					</button>
				</div>
			</ReactModal>
		</div>
	);
};
